import cleanPhoneNumber from "../../methods/cleanPhoneNumber";
import _ from "lodash";
import {
    clientBankAccount,
    clientBankName, clientBIC, clientCorrAccount,
    clientEmail,
    clientFirstName,
    clientLastName, clientLawAddress, clientLawName,
    clientMiddleName, clientOGRN,
    clientPassportIssuedAt,
    clientPassportIssuedBy,
    clientPassportIssuedByCode,
    clientPassportNumber,
    clientPassportPhoto,
    clientPassportRegistration,
    clientPassportRegistrationPhoto,
    clientPassportSelfiePhoto,
    clientPassportType, clientPhoneNumber, clientRole, clientSignature, clientTaxId, PassportType, storeClientData,
    SystemEntity,
    migratePassportTypeListValues,
    normalizePassportType,
    patchClientPassportTypeEntity,
    clientBirthDate,
} from "../../methods/SystemEntity";
import {
    contractSystemEntity,
    dateFromIsoToPlain,
    systemEntityValueByKeyword
} from "../../methods/systemEntityValueByKeyword";
import {clientRoles} from "./UserForms/ChooseAccountTypeFrom";

export function defaultUserFormForContractCreating(contract: any) {
    let clientPassportTypeEntity = contractSystemEntity(contract, clientPassportType.id) ?? clientPassportType
    let clientPassport = normalizePassportType(clientPassportTypeEntity.value ?? "")
    if (clientPassport.length == 0) {
        const selectedValues = migratePassportTypeListValues(clientPassportTypeEntity.selectedValues ?? [])
        if (selectedValues.length === 1) {
            clientPassport = selectedValues[0]
        } else if (selectedValues.length === 0) {
            clientPassport = PassportType.russianPassport.toString()
        } else {
            // Несколько вариантов типа документа: подставляем дефолт как в UI (passportTypeFromSelectedValues)
            clientPassport = passportTypeFromSelectedValues(contract)
        }
    }

    return {
        ...defaultUserFormForSigning(false, contract, {}),
        client_last_name: contractSystemEntity(contract, clientLastName.id)?.value ?? "",
        client_first_name: contractSystemEntity(contract, clientFirstName.id)?.value ?? "",
        client_middle_name: contractSystemEntity(contract, clientMiddleName.id)?.value ?? "",
        client_passport_type: clientPassport,
        client_role: contractSystemEntity(contract, clientRole.id)?.value ?? ''
    }
}

/** Для доп. подписанта подставляем данные из профиля только если в сессии уже есть идентификация — иначе из system_entities слота (как у второй стороны). */
function shouldPreferUserProfileForSigning(user: any): boolean {
    if (!user) {
        return false
    }
    return !!(user.last_name || user.first_name || user.phone_number)
}

/** Данные первой стороны, которые риелтор внёс при выставлении третьим лицам. Не system_entities клиента. */
function contractWithFirstSignerPrefill(contract: any): any | null {
    const prefill = contract?.settings?.first_signer_prefill
    if (!Array.isArray(prefill) || prefill.length === 0) {
        return null
    }
    const hasValue = prefill.some((entity: any) => String(entity?.value ?? '').trim().length > 0)
    if (!hasValue) {
        return null
    }
    return {...contract, system_entities: prefill}
}

export function defaultUserFormForSigning(is_first_signer: boolean, contract: any, user: any, additionalSignerIndex?: number | null) {
    const isAdditionalSigner = additionalSignerIndex != null && additionalSignerIndex >= 0
    const slot = contract?.additional_signers?.[additionalSignerIndex as number]
    const firstPrefillContract = is_first_signer && !isAdditionalSigner
        ? contractWithFirstSignerPrefill(contract)
        : null
    const contractForDefaults = isAdditionalSigner && slot
        ? {...contract, system_entities: slot.system_entities ?? []}
        : (firstPrefillContract ?? contract)

    function userProperty(entity: SystemEntity): string {
        if (!entity.userPath) {
            return ''
        }
        return _.get(user, entity.userPath, '')
    }

    const fromUser = is_first_signer || shouldPreferUserProfileForSigning(user)

    // Паспорт чистится из профиля после подписи — пустой аккаунт не должен затирать SE.
    // Первому подписанту клиентский паспорт из договора не подставляем.
    // Если риелтор заполнил первую сторону, пустые поля профиля берутся оттуда.
    function profileOrContract(entity: SystemEntity, contractValue: string): string {
        if (!fromUser) {
            return contractValue
        }
        const fromProfile = (userProperty(entity) ?? '').toString().trim()
        if (fromProfile) {
            return fromProfile
        }
        if (is_first_signer) {
            return firstPrefillContract ? contractValue : ''
        }
        return contractValue
    }

    function profileTextThenPrefill(entity: SystemEntity, contractFallback: string, format?: (value: string) => string): string {
        if (!fromUser) {
            return contractFallback
        }
        const raw = (userProperty(entity) ?? '').toString()
        if (raw.trim()) {
            return format ? format(raw.trim()) : raw
        }
        if (is_first_signer && firstPrefillContract) {
            return contractFallback
        }
        return format ? format(raw) : raw
    }

    const rawIssuedAtFromUser = (userProperty(clientPassportIssuedAt) ?? '').toString().trim()
    const issuedAtForForm = fromUser && rawIssuedAtFromUser
        ? dateFromIsoToPlain(rawIssuedAtFromUser)
        : is_first_signer
            ? (firstPrefillContract ? defaultIssuedAt(contractForDefaults, null) : '')
            : defaultIssuedAt(contractForDefaults, user)
    return {
        client_last_name: profileTextThenPrefill(clientLastName, defaultLastname(contractForDefaults, null)),
        client_first_name: profileTextThenPrefill(clientFirstName, defaultFirstname(contractForDefaults, null)),
        client_middle_name: profileTextThenPrefill(clientMiddleName, defaultMiddleName(contractForDefaults, null)),
        client_birth_date: profileTextThenPrefill(
            clientBirthDate,
            defaultBirthDate(contractForDefaults, null),
            dateFromIsoToPlain,
        ),

        client_passport_type: defaultPassportType(contractForDefaults , user),
        client_passport_number: profileOrContract(clientPassportNumber, defaultPassportNumber(contractForDefaults, user)),
        client_passport_issued_by_code: profileOrContract(clientPassportIssuedByCode, defaultIssuedByCode(contractForDefaults, user)),
        client_passport_issued_at: issuedAtForForm,
        client_passport_issued_by: profileOrContract(clientPassportIssuedBy, defaultIssuedBy(contractForDefaults, user)),
        client_passport_registration: profileOrContract(clientPassportRegistration, defaultRegistration(contractForDefaults, user)),

        client_signature: profileOrContract(clientSignature, defaultSignature(contractForDefaults, user)),
        
        client_passport_photo: profileOrContract(clientPassportPhoto, defaultPassportPhoto(contractForDefaults, user)),
        client_passport_selfie_photo: profileOrContract(clientPassportSelfiePhoto, defaultPassportSelfiePhoto(contractForDefaults, user)),
        client_passport_registration_photo: profileOrContract(clientPassportRegistrationPhoto, defaultPassportRegistrationPhoto(contractForDefaults, user)),

        /** Отдельные кадры для Beorg (сырой base64, не в системных полях) */
        beorg_verification_passport_b64: '',
        beorg_verification_selfie_b64: '',

        client_law_name: profileTextThenPrefill(clientLawName, defaultLawName(contractForDefaults, null)),
        client_law_address: profileTextThenPrefill(clientLawAddress, defaultLawAddress(contractForDefaults, null)),
        client_tax_id: profileTextThenPrefill(clientTaxId, defaultTaxId(contractForDefaults, null)),
        client_ogrn: profileTextThenPrefill(clientOGRN, defaultOgrn(contractForDefaults, null)),

        client_bank_name: profileTextThenPrefill(clientBankName, defaultBankName(contractForDefaults, null)),
        client_bank_account: profileTextThenPrefill(clientBankAccount, defaultBankAccount(contractForDefaults, null)),
        client_corr_account: profileTextThenPrefill(clientCorrAccount, defaultCorrAccount(contractForDefaults, null)),
        client_bic: profileTextThenPrefill(clientBIC, defaultBic(contractForDefaults, null)),

        client_email: profileTextThenPrefill(clientEmail, defaultEmail(contractForDefaults, null)),
        // E.164 с +. removePrefix срезал +7 до «79» и семёрка кода страны попадала в национальное поле.
        client_phone_number: defaultPhoneNumber(contractForDefaults, user),

        client_role: defaultRole(contractForDefaults, user),

        store_client_data: defaultStoreUserDataValue(contract, is_first_signer),
    }
}

export function defaultRole(contract: any, user: any) {
    if (user) {
        return user.role
    }
    let contractClientRole = contractSystemEntity(contract, clientRole.id) ?? clientRole
    if (contractClientRole.value) {
        return contractClientRole.value
    }
    return contract ? clientRoles[1] : clientRoles[0]
}

export function defaultStoreUserDataValue(contract: any, is_first_signer: boolean): boolean {
    if (!contract) {
        return true
    }

    if (is_first_signer || (!contract.first_signer && !contract.is_mass_template)) {
        return true
    }

    let store_client_data = contractSystemEntity(contract, storeClientData.id)?.value
    
    if (typeof store_client_data !== 'undefined') {
        return store_client_data
    }

    return true
}

export function defaultLastname(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientLastName
    )
}

export function defaultFirstname(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientFirstName
    )
}

export function defaultMiddleName(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientMiddleName
    )
}

export function defaultBirthDate(contract: any, user: any) {
    let value = defaultPropertyValue(
        contract,
        user,
        clientBirthDate
    )
    return dateFromIsoToPlain(value)
}

export function defaultEmail(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientEmail
    )
}

export function defaultPhoneNumber(contract: any, user: any) {
    const entities = contract?.system_entities ?? []
    const clientPhoneNumberEntity = entities.find(
        (e: any) => e.id === clientPhoneNumber.id || e.keyword === clientPhoneNumber.keyword
    )
    if (user && user.phone_number && clientPhoneNumberEntity?.editable != false) {
        return cleanPhoneNumber(user.phone_number)
    }
    const fromContract = defaultPropertyValue(contract, user, clientPhoneNumber)
    return cleanPhoneNumber(fromContract || clientPhoneNumberEntity?.value || '')
}

export function defaultPassportType(contract: any, user: any) {
    const entity = patchClientPassportTypeEntity(
        contractSystemEntity(contract, clientPassportType.id) ?? clientPassportType,
    )
    const selectedValues = migratePassportTypeListValues(entity.selectedValues ?? [])

    let passport_type = defaultPropertyValue(contract, user, clientPassportType)
    if (passport_type && passport_type.length > 0) {
        passport_type = normalizePassportType(passport_type)
        if (selectedValues.length === 0 || selectedValues.includes(passport_type)) {
            return passport_type
        }
    }

    return passportTypeFromSelectedValues(contract)
}

export function passportTypeFromSelectedValues(contract: any): PassportType {
    let entity = contractSystemEntity(contract, clientPassportType.id) ?? clientPassportType
    let selectedValues = migratePassportTypeListValues(entity.selectedValues ?? [])

    if (selectedValues.length == 0) {
        return PassportType.russianPassport
    }

    if (selectedValues.length == 1) {
        return selectedValues[0] as PassportType
    }

    return selectedValues.includes(PassportType.russianPassport) ? PassportType.russianPassport : selectedValues[0] as PassportType
}

export function defaultPassportNumber(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientPassportNumber
    )
}

export function defaultIssuedByCode(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientPassportIssuedByCode
    )
}

export function defaultIssuedAt(contract: any, user: any) {
    let value = defaultPropertyValue(
        contract,
        user,
        clientPassportIssuedAt
    )
    return dateFromIsoToPlain(value)
}

export function defaultIssuedBy(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientPassportIssuedBy
    )
}

export function defaultRegistration(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientPassportRegistration
    )
}

export function defaultSignature(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientSignature
    )
}

export function defaultPassportPhoto(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientPassportPhoto
    )
}

export function defaultPassportSelfiePhoto(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientPassportSelfiePhoto
    )
}

export function defaultPassportRegistrationPhoto(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientPassportRegistrationPhoto
    )
}

export function defaultLawName(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientLawName
    )
}

export function defaultLawAddress(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientLawAddress
    )
}

export function defaultTaxId(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientTaxId
    )
}

export function defaultOgrn(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientOGRN
    )
}

export function defaultBankName(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientBankName
    )
}

export function defaultBankAccount(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientBankAccount
    )
}

export function defaultCorrAccount(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientCorrAccount
    )
}

export function defaultBic(contract: any, user: any) {
    return defaultPropertyValue(
        contract,
        user,
        clientBIC
    )
}



export function defaultPropertyValue(
    contract: any,
    user: any,
    systemEntity: SystemEntity | null
) {
    if (!systemEntity) {
        return ''
    }
    let value = systemEntityValueByKeyword(contract, systemEntity.keyword, systemEntity.id)

    if (value && value.length > 0) {
        return value
    } else {
        if (systemEntity.userPath && systemEntity.userPath.length > 0) {
            return _.get(user, systemEntity.userPath, '')
        } else {
            return ''
        }
    }
}